import { useEffect, useMemo, useRef, useState } from "react";
import {
  RequestCancelInboundPlanRequest,
  RequestCancelInboundPlansRequest,
  RequestGetPlanInfoDetailRequest,
  RequestGetPlanInfoListRequest,
  ResponseGetPlanInfoListResponse,
} from "../../services/OpenAPI";
import { ShipmentItem } from "../../components/PendingShipment/PendingShipmentTable";
import { formatDate } from "../../utils/dateFormat";
import { loginUserSelector } from "../../recoil/selectors/loginUserSelector";
import { isGeneralUser, isNotGeneralUser } from "../../constants/roles";
import { useRecoilValue, useResetRecoilState, useSetRecoilState } from "recoil";
import { userSearchOptionsSelector } from "../../recoil/selectors/userSearchOptionsSelector";
import { pendingPlanUpdatedFlagState } from "../../recoil/atoms/pendingPlanAtoms";
import { useApiGetPlanInfoList } from "../api/useApiGetPlanInfoList";
import { useApiGetPlanInfoDetail } from "../api/useApiGetPlanInfoDetail";
import { pendingShipmentUpdatedFlagState } from "../../recoil/atoms/pendingShipmentAtoms";
import { useApiUpdateAmazonItemsStatus } from "../api/useApiUpdateAmazonItemsStatus";
import { useApiCancelInboundPlan } from "../api/useApiCancelInboundPlan";
import { lwaNotConnectedMessage } from "../../utils/lwaError";
import { pendingShipmentDetailUpdatedFlagState } from "../../recoil/atoms/pendingShipmentDetailAtoms";

export type SearchGenreOption = {
  id: string;
  label: string;
};

export const usePendingShipment = () => {
  const searchGenres: SearchGenreOption[] = [
    { id: "planName", label: "プラン名" },
    { id: "shipNum", label: "問い合わせNO" },
    { id: "isCompleted", label: "記入済み" },
  ];
  const inputRef = useRef<HTMLInputElement>(null);

  const { planInfoList, fetchPlanInfoList } = useApiGetPlanInfoList();
  const { planInfoDetail, fetchPlanInfoDetail } = useApiGetPlanInfoDetail();
  const { updateAmazonItemsStatus } = useApiUpdateAmazonItemsStatus();
  const { cancelInboundPlan, isLoading: isCancelLoading } =
    useApiCancelInboundPlan();
  const loginUser = useRecoilValue(loginUserSelector);
  const userSearchOptions = useRecoilValue(userSearchOptionsSelector);
  const setPendingPlanUpdatedFlag = useSetRecoilState(
    pendingPlanUpdatedFlagState
  );
  const pendingPlanUpdatedFlag = useRecoilValue(pendingPlanUpdatedFlagState);
  const resetPendingPlanUpdatedFlag = useResetRecoilState(
    pendingPlanUpdatedFlagState
  );
  const setPendingShipmentUpdatedFlag = useSetRecoilState(
    pendingShipmentUpdatedFlagState
  );
  const pendingShipmentUPdatedFlas = useRecoilValue(
    pendingShipmentUpdatedFlagState
  );
  const resetPendingShipmentUpdatedFlag = useResetRecoilState(
    pendingShipmentUpdatedFlagState
  );
  const pendingShipmentDetailUpdatedFlag = useRecoilValue(
    pendingShipmentDetailUpdatedFlagState
  );
  const resetPendingShipmentDetailUpdatedFlag = useResetRecoilState(
    pendingShipmentDetailUpdatedFlagState
  );

  const [pendingShipmentList, setPendingShipmentList] = useState<
    ShipmentItem[]
  >([]);
  const [totalListCount, setTotalListCount] = useState(0);
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [checkedIds, setCheckedIds] = useState<Set<string>>(new Set());
  const [searchKeyword, setSearchKeyword] = useState("");
  const [planName, setPlanName] = useState("");
  const [shipNum, setShipNum] = useState("");
  const [isCompleted, setIsCompleted] = useState(false);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState<ShipmentItem | null>(null);
  const [toastOpen, setToastOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastSeverity, setToastSeverity] = useState<"success" | "error">(
    "success"
  );
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(50);

  const handleCloseToast = () => setToastOpen(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // チェックされたIDの更新ハンドラ
  const handleCheckChange = (newChecked: Set<string>) => {
    setCheckedIds(newChecked);
  };

  const [selectedGenreId, setSelectedGenreId] = useState(
    searchGenres[0]?.label ?? ""
  );

  const resetGenreWord = () => {
    setPlanName("");
    setShipNum("");
    setIsCompleted(false);
    setSearchKeyword("");
    return new Promise<void>((resolve) => {
      setPlanName("");
      setShipNum("");
      setIsCompleted(false);
      setSearchKeyword("");

      // 状態更新の直後に resolve（1フレーム後に確実に）
      requestAnimationFrame(() => resolve());
    });
  };

  const resetFilter = async () => {
    if (isNotGeneralUser(loginUser?.permissionId)) {
      setSelectedUserId(null);
    }
    await resetGenreWord();
    setPendingShipmentUpdatedFlag(true);
    return new Promise<void>((resolve) => {
      if (isNotGeneralUser(loginUser?.permissionId)) {
        setSelectedUserId(null);
      }
    });
  };

  const handleGenreChange = (genreId: string) => {
    setSelectedGenreId(genreId);
    resetGenreWord();
    setIsCompleted(genreId === "記入済");
  };

  const transformData = (
    data: ResponseGetPlanInfoListResponse[] | undefined
  ): ShipmentItem[] => {
    if (!Array.isArray(data)) return [];
    const result: ShipmentItem[] = [];

    data.forEach((plans) => {
      result.push({
        id: Number(plans.planId),
        planCreatedDate: formatDate(plans.createdAt),
        userId: plans.userId,
        user: plans.userName,
        shipmentConfirmId: plans.shipmentConfirmationId,
        planName: plans.planName,
        groupNum: plans.gorupNum,
        productCount: plans.productCount,
        skuCount: plans.skuCount,
        singleCount: plans.singleCount,
        multiCount: plans.multiCount,
      });
    });

    return result;
  };

  const handleParentCheck = (parentId: string, childIds: string[]) => {
    // const newSet = new Set<string>([parentId, ...childIds]);
    const newSet = new Set<string>([parentId]);
    handleCheckChange(newSet);
  };

  const handleKeywordChange = (keyword: string) => {
    // 一旦処理なし
  };

  const focusInputField = () => {
    inputRef.current?.focus();
  };

  const handleRowClick = async (item: ShipmentItem) => {
    setSelectedRow(item);
    if (loginUser?.tenantId) {
      const req: RequestGetPlanInfoDetailRequest = {
        tenantId: loginUser.tenantId,
        baseId: loginUser.baseId!,
        planId: Number(item.id),
        statusId: 4,
      };
      await fetchPlanInfoDetail(req);
    }
    // setDetailDialogOpen(true);
  };

  const handleUserChange = (userId: number | null) => {
    setSelectedUserId(userId);
    // ここでフィルタ処理やAPI呼び出し
  };

  const userOptions = useMemo(() => {
    return (
      userSearchOptions?.map((user) => ({
        label: `${user.userNumber ?? ""}_${user.lastName ?? ""}${
          user.firstName ?? ""
        }`,
        id: user.id!,
      })) ?? []
    );
  }, [userSearchOptions]);

  const fetchData = async (req: RequestGetPlanInfoListRequest) => {
    const response = await fetchPlanInfoList(req);
    if (response) {
      const transformed = transformData(response);
      // setTotalListCount(response.totalCount ?? 0);
      setPendingShipmentList(transformed);
    }
  };

  useEffect(() => {
    if (loginUser) {
      if (loginUser.tenantId) {
        const req: RequestGetPlanInfoListRequest = {
          statusId: 4,
          tenantId: loginUser.tenantId,
          baseId: loginUser.baseId!,
          userId: loginUser.id ?? 0,
          createdUserId:
            isGeneralUser(loginUser?.permissionId) ? loginUser.id! : undefined,
        };
        fetchData(req);
      }
      if (isGeneralUser(loginUser?.permissionId)) {
        setSelectedUserId(loginUser.id!);
      }
    }
  }, [loginUser]);

  useEffect(() => {
    if (pendingShipmentUPdatedFlas) {
      if (loginUser?.tenantId) {
        const req: RequestGetPlanInfoListRequest = {
          statusId: 4,
          tenantId: loginUser.tenantId,
          baseId: loginUser.baseId!,
          userId: loginUser.id ?? 0,
          createdUserId:
            isGeneralUser(loginUser?.permissionId) ? loginUser.id! : undefined,
        };
        fetchData(req);
      }
      resetPendingShipmentUpdatedFlag();
    }
  }, [pendingShipmentUPdatedFlas, fetchData]);

  useEffect(() => {
    switch (selectedGenreId) {
      case searchGenres[0].label:
        setPlanName(searchKeyword);
        break;
      case searchGenres[1].label:
        setShipNum(searchKeyword);
        break;
    }
    handleKeywordChange(searchKeyword);
  }, [searchKeyword]);

  const searchItems = (searchKeyword: string) => {
    if (loginUser?.tenantId) {
      const req: RequestGetPlanInfoListRequest = {
        statusId: 4,
        tenantId: loginUser.tenantId,
        baseId: loginUser.baseId!,
        userId: loginUser.id ?? 0,
        createdUserId: selectedUserId ?? undefined,

        planName:
          searchGenres[0].id && planName !== "" ? searchKeyword : undefined,
        shipNum:
          searchGenres[1].id && shipNum !== "" ? searchKeyword : undefined,
        isCompleted: searchGenres[2].id && isCompleted ? true : undefined,
      };
      fetchData(req);
    }
  };

  useEffect(() => {
    if (planInfoDetail) {
      setDetailDialogOpen(true);
    } else {
      setDetailDialogOpen(false);
    }
  }, [planInfoDetail]);

  const handleBackStatus = async () => {
    const plansMap = new Map<number, number[]>();

    Array.from(checkedIds).forEach((rawId) => {
      const idNum = Number(String(rawId));
      const matched = pendingShipmentList.find((item) => {
        const itemIdNum = Number(String(item.id));
        return itemIdNum === idNum;
      });

      if (!matched || !matched.userId) return;

      if (!plansMap.has(matched.userId)) {
        plansMap.set(matched.userId, []);
      }

      plansMap.get(matched.userId)!.push(idNum);
    });

    const plans: RequestCancelInboundPlanRequest[] = Array.from(
      plansMap.entries()
    ).map(([userId, ids]) => ({
      id: ids,
      tenantId: loginUser!.tenantId,
      userId,
    }));

    const req: RequestCancelInboundPlansRequest = {
      plans,
      updateUserId: loginUser!.id ?? 0,
    };

    const { ok, notConnected } = await cancelInboundPlan(req);

    if (ok) {
      setToastMessage(`${Array.from(checkedIds).length}件更新しました`);
      setToastSeverity("success");
      setToastOpen(true);
      setPendingShipmentUpdatedFlag(true);
    } else if (notConnected) {
      setToastMessage(lwaNotConnectedMessage());
      setToastSeverity("error");
      setToastOpen(true);
    } else {
      setToastMessage("更新に失敗しました");
      setToastSeverity("error");
      setToastOpen(true);
    }
  };

  useEffect(() => {
    const reFetch = async () => {
      if (loginUser?.tenantId) {
        const req: RequestGetPlanInfoDetailRequest = {
          tenantId: loginUser.tenantId,
          baseId: loginUser.baseId!,
          planId: planInfoDetail?.planId,
          statusId: 4,
        };
        await fetchPlanInfoDetail(req);
      }
    };
    if (pendingShipmentDetailUpdatedFlag) {
      reFetch();
    }
    resetPendingShipmentDetailUpdatedFlag();
  }, [pendingShipmentDetailUpdatedFlag]);

  return {
    inputRef,
    loginUser,
    pendingShipmentList,
    userOptions,
    selectedUserId,
    handleUserChange,
    checkedIds,
    handleCheckChange,
    searchGenres,
    selectedGenreId,
    setSelectedGenreId,
    focusInputField,
    handleGenreChange,
    searchKeyword,
    setSearchKeyword,
    searchItems,
    resetFilter,
    resetGenreWord,
    handleKeywordChange,
    handleRowClick,
    detailDialogOpen,
    setDetailDialogOpen,
    toastOpen,
    toastMessage,
    toastSeverity,
    handleCloseToast,
    fileInputRef,
    planInfoDetail,
    handleBackStatus,
    isCancelLoading,
    totalListCount,
  };
};
